'use client';

import React from 'react';
import NavBar from '@/components/header/NavBar';

import Why from '@/components/home-page/whySection';
import ContactForm from '@/components/home-page/ContactForm';
import FAQContainer from '@/components/home-page/FAQContainer';
import Footer from '@/components/footer/Footer';
import Image from "next/image";
import { FaAngleDoubleRight } from "react-icons/fa";

const features = [
  {
    title: "Full Affiliate Program",
    description:
      "Affiliates generate their own referral link in seconds, track every click and conversion in real time, and see exactly which sales came from their audience.",
  },
  {
    title: "Sales History & Analytics",
    description:
      "A dedicated affiliate dashboard shows a full history of sales, commissions earned, and performance over time, so affiliates always know where they stand.",
  },
  {
    title: "Built-In Withdrawal System",
    description:
      "Affiliates can request and track withdrawals directly from their dashboard, with no manual payout requests or spreadsheets needed on either side.",
  },
  {
    title: "BTCPay & Paddle Payments",
    description:
      "Customers can check out with card payments through Paddle or pay in cryptocurrency through a self-hosted BTCPay Server integration, covering both traditional and crypto buyers.",
  },
  {
    title: "Large Ebook Pack Delivery",
    description:
      "The platform reliably delivers large ebook bundles on purchase, with secure, instant digital downloads built to handle bigger file sizes without friction.",
  },
  {
    title: "10-Language Experience",
    description:
      "The storefront, checkout, and affiliate dashboard are all fully localized across 10 languages, letting Padel Master The Game sell and recruit affiliates globally.",
  },
];

const gallery = [
  {
    src: "/padel-master/1.avif",
    title: "Affiliate Dashboard",
    content:
      "Affiliates get their referral link, live click and conversion stats, and commission totals in one clean dashboard, built to make promoting the ebook effortless.",
  },
  {
    src: "/padel-master/2.avif",
    title: "Sales & Payout History",
    content:
      "Every sale and every payout is logged and searchable, giving affiliates full transparency and giving Padel Master The Game a clear audit trail.",
  },
  {
    src: "/padel-master/3.avif",
    title: "Checkout & Delivery",
    content:
      "A fast checkout supporting card and crypto payments, followed by instant, secure delivery of the full ebook pack straight to the customer.",
  },
];

const stack = {
  Frontend: ["TypeScript", "Next.js", "Tailwind CSS"],
  Backend: ["Django", "Celery", "PostgreSQL"],
};

const ContactForms: React.FC = () => {
  return (
    <div className="">
      {/* Hero */}
      <div
        className="bg-cover bg-center bg-no-repeat h-96 lg:h-[600px] mb-2 relative "
        style={{ backgroundImage: `url('/hero/padel-game.avif')` }}
      >
        <div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[600px] " />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 gap-4 lg:gap-6">
          <p className="text-white/70 text-sm lg:text-base tracking-wide">
            Ebook Platform · Affiliate System
          </p>
          <h1 className="text-white text-5xl lg:text-9xl font-extrabold">
            Padel Master
          </h1>
          <p className="text-white/80 text-lg lg:text-2xl max-w-2xl">
            An ebook platform with a full affiliate program, modern payments,
            and a storefront built to sell in 10 languages.
          </p>
          <a
            href="https://padelmasterthegame.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 lg:mt-4 inline-flex items-center gap-2 bg-white text-primary font-semibold px-6 py-3 rounded-full text-sm lg:text-base hover:bg-white/90 transition-colors"
          >
            Visit padelmasterthegame.com <FaAngleDoubleRight size={14} />
          </a>
        </div>
      </div>

      <NavBar />

      {/* Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 bg-background">
        <div className="relative w-full h-72 lg:h-auto">
          <Image
            src="/padel-master/4.avif"
            alt="Padel Master The Game overview"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover"
          />
        </div>
        <div className="space-y-6 lg:space-y-8 p-6 md:p-12 lg:p-24 flex flex-col justify-center">
          <h2 className="text-3xl lg:text-5xl font-extrabold text-primary">
            Project Overview
          </h2>
          <p className="max-w-prose text-base lg:text-xl leading-relaxed text-neutral-600">
            Padel Master The Game needed more than a simple digital
            storefront. Selling a large ebook pack meant the platform had to
            handle reliable large-file delivery, accept both card and
            cryptocurrency payments, and — most importantly — power a real
            affiliate business, not just a "share this link" feature bolted
            onto a checkout page.
          </p>
          <p className="max-w-prose text-base lg:text-xl leading-relaxed text-neutral-600">
            We built the entire affiliate journey from scratch: link
            generation, click and sales tracking, commission calculation, and
            self-service withdrawals, all wrapped in a Next.js and TypeScript
            frontend backed by Django and Celery for reliable automation
            behind the scenes.
          </p>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-primary py-12 lg:py-20 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4 text-center">
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">10</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Languages supported</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">2</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Payment gateways: BTCPay & Paddle</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">↻</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Full affiliate lifecycle, link to withdrawal</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">⇩</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Instant large ebook pack delivery</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-background p-6 md:p-12 lg:p-24">
        <h2 className="text-3xl lg:text-6xl font-extrabold text-primary mb-8 lg:mb-16">
          What We Built
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
          {features.map((feature, i) => (
            <div
              key={i}
              className="px-5 py-5 lg:px-6 lg:py-6 border border-dashed border-secondary rounded-2xl"
            >
              <h3 className="text-lg lg:text-xl font-bold mb-2">
                {feature.title}
              </h3>
              <p className="text-sm lg:text-base text-neutral-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-secondary text-white p-6 md:p-12 lg:p-24">
        <h2 className="text-3xl lg:text-6xl font-extrabold mb-8 lg:mb-16">
          A Closer Look
        </h2>
        <div className="space-y-12 lg:space-y-20">
          {gallery.map((item, i) => {
            const isReversed = i % 2 === 1;
            return (
              <div
                key={i}
                className={`flex flex-col lg:gap-16 items-center ${
                  isReversed ? "lg:flex-row-reverse" : "lg:flex-row"
                }`}
              >
                <div className="relative w-full h-64 sm:h-96 lg:h-[500px] rounded-3xl overflow-hidden shadow-lg">
                  <Image
                    src={item.src}
                    alt={item.title}
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="object-cover rounded-3xl"
                  />
                </div>
                <div className="w-full space-y-3 lg:space-y-6 mt-6 lg:mt-0">
                  <h4 className="text-2xl lg:text-4xl font-bold">
                    {item.title}
                  </h4>
                  <p className="text-base lg:text-xl leading-relaxed text-neutral-600">
                    {item.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tech stack */}
      <div className="bg-primary p-6 md:p-12 lg:p-24 text-white">
        <h2 className="text-3xl lg:text-6xl font-extrabold mb-8 lg:mb-16">
          Tech Stack
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {Object.entries(stack).map(([group, items]) => (
            <div key={group}>
              <p className="text-white/60 text-sm lg:text-base mb-4">{group}</p>
              <div className="flex flex-wrap gap-2 lg:gap-3">
                {items.map((item) => (
                  <span
                    key={item}
                    className="px-4 py-2 rounded-full border border-white/30 text-sm lg:text-base"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default ContactForms;